import React, { useMemo, useState } from "react"
import { ChevronDown, ChevronUp, Plus } from "lucide-react"

const STATUS_SUMMARY_ORDER = [
  "initiated",
  "pre_observation_submitted",
  "observations_captured",
  "submitted_by_admin",
  "completed",
]

const StatusBadge = ({ label, statusClass }) => (
  <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${statusClass}`}>
    {label}
  </span>
)

const SiteVisitsAdminList = ({
  adminStaff = [],
  loading = false,
  statusByEmail = {},
  statusesLoading = false,
  currentYear,
  getStatusLabel,
  getStatusClass,
  onInitiateSiteVisit,
  onCaptureObservations,
  onCompleteEvaluation,
  onReinitiateSiteVisit,
  onViewSiteVisit,
}) => {
  const [searchQuery, setSearchQuery] = useState("")
  const [expandedRowId, setExpandedRowId] = useState(null)

  const sortNewestFirst = (a, b) => {
    const aVisit = Number(a?.visitNumber || 0)
    const bVisit = Number(b?.visitNumber || 0)
    if (a?.year !== b?.year) {
      return Number(b?.year || 0) - Number(a?.year || 0)
    }
    if (aVisit !== bVisit) return bVisit - aVisit

    const aTime = a?.createdAt ? new Date(a.createdAt).getTime() : Number(a?.id || 0)
    const bTime = b?.createdAt ? new Date(b.createdAt).getTime() : Number(b?.id || 0)
    return bTime - aTime
  }

  const filteredStaff = useMemo(() => {
    const query = searchQuery.trim().toLowerCase()
    if (!query) return adminStaff

    return adminStaff.filter((admin) => {
      const fullName = `${admin.firstName || ""} ${admin.lastName || ""}`.toLowerCase()
      const email = (admin.email || "").toLowerCase()
      return fullName.includes(query) || email.includes(query)
    })
  }, [adminStaff, searchQuery])

  const handleToggleRow = (adminId) => {
    setExpandedRowId((current) => (current === adminId ? null : adminId))
  }

  const getAdminVisits = (admin) => {
    const data = statusByEmail[admin.email?.toLowerCase()] || { siteVisits: [] }
    return [...(data.siteVisits || [])].sort(sortNewestFirst)
  }

  const getStatusSummary = (visits) => {
    const counts = visits.reduce((acc, visit) => {
      const key = visit.status || "unknown"
      acc[key] = (acc[key] || 0) + 1
      return acc
    }, {})

    const knownStatuses = STATUS_SUMMARY_ORDER.filter((status) => counts[status]).map(
      (status) => ({
        status,
        count: counts[status],
      })
    )

    const extraStatuses = Object.keys(counts)
      .filter((status) => !STATUS_SUMMARY_ORDER.includes(status))
      .map((status) => ({
        status,
        count: counts[status],
      }))

    return [...knownStatuses, ...extraStatuses]
  }

  const getVisitActions = (admin, visit) => {
    if (visit.status === "pre_observation_submitted") {
      return (
        <button
          type="button"
          onClick={() => onCaptureObservations(admin, visit)}
          className="px-3 py-1 bg-[#ED7D31] text-white rounded text-xs hover:opacity-90"
          aria-label={`Capture observation evidence for visit ${visit.visitNumber || visit.id}`}
        >
          Capture Observation Evidence
        </button>
      )
    }

    if (visit.status === "submitted_by_admin") {
      return (
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => onCompleteEvaluation(admin, visit)}
            className="px-3 py-1 bg-[#241763] text-white rounded text-xs hover:bg-[#1a1047]"
            aria-label={`Complete evaluation for visit ${visit.visitNumber || visit.id}`}
          >
            Complete Evaluation
          </button>
          {onReinitiateSiteVisit && (
            <button
              type="button"
              onClick={() => onReinitiateSiteVisit(admin, visit)}
              className="px-3 py-1 bg-white border border-gray-300 text-gray-700 rounded text-xs hover:bg-gray-50"
              aria-label={`Reinitiate visit ${visit.visitNumber || visit.id}`}
            >
              Reinitiate
            </button>
          )}
        </div>
      )
    }

    if (visit.status === "initiated") {
      return (
        <span className="text-xs text-gray-500">
          Waiting for admin to complete Pre-Observation
        </span>
      )
    }

    if (visit.status === "observations_captured") {
      return (
        <span className="text-xs text-gray-500">
          Waiting for admin to complete Post-Observation
        </span>
      )
    }

    return null
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h3 className="font-semibold text-[#241763]">Admin Staff</h3>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search by name or email..."
          className="w-full sm:w-72 p-2 border rounded text-sm"
          aria-label="Search admin staff"
        />
      </div>

      <div className="border rounded-lg overflow-x-auto">
        <table className="w-full text-sm min-w-[760px]">
          <thead className="bg-gray-100 font-bold">
            <tr>
              <th className="p-3 text-left">Name</th>
              <th className="p-3 text-left">Email</th>
              <th className="p-3 text-left">Observation / Site Visit</th>
              <th className="p-3 text-left">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading || statusesLoading ? (
              <tr>
                <td colSpan={4} className="p-4 text-center text-gray-500">
                  Loading admin staff and statuses...
                </td>
              </tr>
            ) : filteredStaff.length === 0 ? (
              <tr>
                <td colSpan={4} className="p-4 text-center text-gray-500">
                  No admin staff found.
                </td>
              </tr>
            ) : (
              filteredStaff.map((admin) => {
                const visits = getAdminVisits(admin)
                const statusSummary = getStatusSummary(visits)
                const isExpanded = expandedRowId === admin.id
                const currentYearCount = visits.filter(
                  (visit) => visit.year === currentYear
                ).length

                return (
                  <React.Fragment key={admin.id}>
                    <tr className="border-t hover:bg-gray-50">
                      <td className="p-3 font-medium whitespace-nowrap">
                        {admin.firstName} {admin.lastName}
                      </td>
                      <td className="p-3 text-gray-600">{admin.email}</td>
                      <td className="p-3">
                        <div className="flex flex-col gap-2 items-start">
                          {statusSummary.length === 0 ? (
                            <StatusBadge
                              label="Not Started"
                              statusClass="bg-gray-100 text-gray-600"
                            />
                          ) : (
                            <div className="flex flex-wrap gap-1">
                              {statusSummary.map(({ status, count }) => (
                                <StatusBadge
                                  key={status}
                                  label={`${getStatusLabel(status)}${
                                    count > 1 ? ` (${count})` : ""
                                  }`}
                                  statusClass={getStatusClass(status)}
                                />
                              ))}
                            </div>
                          )}
                          <button
                            type="button"
                            onClick={() => onInitiateSiteVisit(admin)}
                            className="flex items-center gap-1 px-3 py-1 bg-[#4472C4] text-white rounded text-xs hover:opacity-90"
                            aria-label={`Initiate site visit for ${admin.firstName} ${admin.lastName}`}
                          >
                            <Plus className="h-3 w-3" />
                            {visits.length > 0
                              ? "Initiate Another Site Visit"
                              : "Initiate Site Visit"}
                          </button>
                          {visits.length > 0 && (
                            <span className="text-xs text-gray-500">
                              {visits.length} visit{visits.length === 1 ? "" : "s"}
                              {currentYearCount > 0
                                ? ` (${currentYearCount} this year)`
                                : ""}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="p-3">
                        <button
                          type="button"
                          onClick={() => handleToggleRow(admin.id)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault()
                              handleToggleRow(admin.id)
                            }
                          }}
                          className="cursor-pointer hover:bg-[#241763]/60 group bg-[#B6A5C9]/50 p-2 rounded"
                          aria-label={
                            isExpanded
                              ? `Hide site visits for ${admin.firstName} ${admin.lastName}`
                              : `Show site visits for ${admin.firstName} ${admin.lastName}`
                          }
                          aria-expanded={isExpanded}
                        >
                          {isExpanded ? (
                            <ChevronUp size={16} className="group-hover:text-white" />
                          ) : (
                            <ChevronDown size={16} className="group-hover:text-white" />
                          )}
                        </button>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr className="bg-gray-50">
                        <td colSpan={4} className="p-4">
                          {visits.length === 0 ? (
                            <div className="text-sm text-gray-500">
                              No site visits found for this administrator.
                            </div>
                          ) : (
                            <div className="bg-white border rounded-lg overflow-x-auto">
                              <table className="w-full text-sm">
                                <thead className="bg-gray-100">
                                  <tr>
                                    <th className="p-2 text-left">Visit #</th>
                                    <th className="p-2 text-left">Year</th>
                                    <th className="p-2 text-left">Status</th>
                                    <th className="p-2 text-left">Actions</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {visits.map((visit) => (
                                    <tr key={visit.id} className="border-t hover:bg-gray-50">
                                      <td className="p-2 font-medium">
                                        #{visit.visitNumber || visit.id}
                                      </td>
                                      <td className="p-2">{visit.year}</td>
                                      <td className="p-2">
                                        <StatusBadge
                                          label={getStatusLabel(visit.status)}
                                          statusClass={getStatusClass(visit.status)}
                                        />
                                      </td>
                                      <td className="p-2">
                                        <div className="flex flex-wrap items-center gap-2">
                                          {getVisitActions(admin, visit)}
                                          <button
                                            type="button"
                                            onClick={() => onViewSiteVisit(admin, visit)}
                                            className="px-3 py-1 bg-gray-200 text-gray-700 rounded text-xs hover:bg-gray-300"
                                            aria-label={`View site visit ${visit.visitNumber || visit.id}`}
                                          >
                                            View
                                          </button>
                                        </div>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                )
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default SiteVisitsAdminList
